/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/

import React from 'react';
import cx from 'classnames';

import { get, isEmpty, map } from 'lodash';
import { unstable } from '@broadleaf/admin-components';
import { getOrderItemThumbnailUrl } from 'app/oms/components/utils/omsUtils';
import PopoverLabel from '../Popover/PopoverLabel';
import { FormattedMessage } from 'react-intl';
import messages from './FulfillmentCardItem.messages';

const { useFormatMessage, Currency, Image, MetadataUtils, SVG } = unstable;
const { getAttribute } = MetadataUtils;

const AuroraFulfillmentCardItem = ({
  fulfillmentItem,
  itemsMetadata,
  orderItem
}) => {
  const thumbnailUrl = getOrderItemThumbnailUrl(orderItem);
  const distinguishPriceType = getAttribute(
    itemsMetadata,
    'distinguishPriceType',
    false
  );
  const priceInfoPath = getAttribute(itemsMetadata, 'priceInfoPath');
  return (
    <div className="tw-flex tw-flex-col tw-items-center md:tw-flex-row md:tw-items-start">
      <div className="tw-mb-3 tw-h-32 tw-w-32 tw-flex-initial md:tw-mb-0 md:tw-h-24 md:tw-w-24">
        <Image
          alt={orderItem.name}
          className="tw-max-h-full tw-w-full"
          fallback={
            <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-gray-100">
              <SVG
                className="tw-h-1/2 tw-w-1/2 tw-fill-current tw-text-gray-400"
                name="photo"
              />
            </div>
          }
          src={thumbnailUrl}
        />
      </div>
      <div className="tw-flex tw-w-full tw-flex-1 tw-flex-row tw-overflow-hidden md:tw-ml-3 md:tw-w-auto">
        <div className="tw-flex-1 tw-overflow-hidden">
          <div
            className="tw-truncate tw-text-lg tw-font-semibold tw-leading-tight tw-text-gray-800"
            title={orderItem.name}
          >
            {orderItem.name}
          </div>
          <div className="tw-truncate tw-text-sm tw-text-gray-700">
            {orderItem.sku}
          </div>
          <Price
            fulfillmentItem={fulfillmentItem}
            distinguishPriceType={distinguishPriceType}
            orderItem={orderItem}
            priceInfoPath={priceInfoPath}
          />
          <AttributeChoices orderItem={orderItem} />
          {!isEmpty(orderItem.itemAdjustments) && (
            <PromotionsApplied
              fulfillmentItem={fulfillmentItem}
              orderItem={orderItem}
            />
          )}
        </div>
        <div className="tw-ml-3 tw-flex-initial">
          <FulfillmentItemTotal fulfillmentItem={fulfillmentItem} />
        </div>
      </div>
    </div>
  );
};

const Price = ({
  distinguishPriceType,
  priceInfoPath,
  fulfillmentItem,
  orderItem
}) => {
  const priceInfo = get(
    orderItem,
    priceInfoPath,
    get(fulfillmentItem, priceInfoPath)
  );
  const { unitPrice, unitPriceType } = orderItem;
  const distinguishSalePrice =
    distinguishPriceType && unitPriceType === 'salePrice';
  const basePrice = get(
    priceInfo,
    'priceTypeDetails.standardPrice.bestPrice',
    get(priceInfo, 'priceTypeDetails.basePrice.bestPrice')
  );
  return (
    <div className="tw-mt-1 tw-text-sm tw-text-gray-700 md:tw-text-base">
      <span
        className={cx({
          'tw-font-medium tw-text-red-600': distinguishSalePrice
        })}
      >
        <Currency currency={unitPrice.currency} value={unitPrice.amount} />
      </span>{' '}
      {distinguishSalePrice && !!basePrice && (
        <span className="tw-text-xs tw-line-through md:tw-text-sm">
          <Currency currency={basePrice.currency} value={basePrice.amount} />
        </span>
      )}
      {fulfillmentItem.quantity > 1 && ` x ${fulfillmentItem.quantity}`}
    </div>
  );
};

const FulfillmentItemTotal = ({ fulfillmentItem }) => {
  const formatMessage = useFormatMessage();
  const {
    merchandiseAdjustmentsTotal,
    merchandiseSubtotal,
    merchandiseTaxTotal,
    coreCostTotal,
    proratedOrderAdjustments
  } = fulfillmentItem;
  const itemTotal = getItemTotal(fulfillmentItem);
  return (
    <PopoverLabel
      label={
        <Currency currency={itemTotal.currency} value={itemTotal.amount} />
      }
      position="bottom-right"
    >
      <div className="tw-p-2">
        <ItemTotalDetail
          className="tw-mb-1 tw-text-sm"
          label={
            <FormattedMessage id="OrderView.FulfillmentCard.itemMerchandiseSubtotal" />
          }
        >
          <Currency
            currency={merchandiseSubtotal.currency}
            value={merchandiseSubtotal.amount}
          />
        </ItemTotalDetail>
        {!!coreCostTotal && coreCostTotal.amount > 0 && (
          <ItemTotalDetail
            className="tw-mb-1 tw-text-sm"
            label={formatMessage(messages.coreCostTotal)}
          >
            <Currency
              currency={coreCostTotal.currency}
              value={coreCostTotal.amount}
            />
          </ItemTotalDetail>
        )}
        {!!merchandiseAdjustmentsTotal &&
          merchandiseAdjustmentsTotal.amount > 0 && (
            <ItemTotalDetail
              className="tw-mb-1 tw-text-sm tw-text-red-600"
              label={
                <FormattedMessage id="OrderView.FulfillmentCard.itemMerchandiseAdjustmentsTotal" />
              }
            >
              <Currency
                currency={merchandiseAdjustmentsTotal.currency}
                value={-merchandiseAdjustmentsTotal.amount}
              />
            </ItemTotalDetail>
          )}
        {!!proratedOrderAdjustments && proratedOrderAdjustments.amount > 0 && (
          <ItemTotalDetail
            className="tw-mb-1 tw-text-sm tw-text-red-600"
            label={formatMessage(messages.proratedOrderAdjustments)}
          >
            <Currency
              currency={proratedOrderAdjustments.currency}
              value={-proratedOrderAdjustments.amount}
            />
          </ItemTotalDetail>
        )}

        <ItemTotalDetail
          className="tw-mb-1 tw-text-sm"
          label={
            <FormattedMessage id="OrderView.FulfillmentCard.itemMerchandiseTaxTotal" />
          }
        >
          <Currency
            currency={merchandiseTaxTotal.currency}
            value={merchandiseTaxTotal.amount}
          />
        </ItemTotalDetail>
      </div>
      <hr className="tw-border-gray-300" />
      <div className="tw-p-2">
        <ItemTotalDetail
          className="tw-text-base"
          label={formatMessage(messages.merchandiseTotal)}
        >
          <Currency currency={itemTotal.currency} value={itemTotal.amount} />
        </ItemTotalDetail>
      </div>
    </PopoverLabel>
  );
};

function getItemTotal(fulfillmentItem) {
  const { merchandiseTaxTotal, merchandiseTotal, proratedOrderAdjustments } =
    fulfillmentItem;
  let totalAmount = merchandiseTotal.amount;
  totalAmount = isEmpty(proratedOrderAdjustments)
    ? totalAmount
    : totalAmount - proratedOrderAdjustments.amount;
  totalAmount = isEmpty(merchandiseTaxTotal)
    ? totalAmount
    : totalAmount + merchandiseTaxTotal.amount;
  return {
    currency: merchandiseTotal.currency,
    amount: totalAmount
  };
}

const PromotionsApplied = ({ fulfillmentItem, orderItem }) => {
  const { itemAdjustments = [] } = orderItem;
  const { merchandiseAdjustmentsTotal } = fulfillmentItem;
  return (
    <div className="tw-mt-2">
      <PopoverLabel
        label={
          <FormattedMessage id="OrderView.FulfillmentCard.promotionsApplied" />
        }
        position="bottom-left"
      >
        <div className="tw-p-2">
          {itemAdjustments.map((itemAdjustment, index) => {
            const { amount, codeUsed, offerRef } = itemAdjustment;
            const label = codeUsed || (offerRef && offerRef.name) || '---';
            return (
              <ItemTotalDetail
                key={index}
                className="tw-mb-1 tw-text-xs"
                label={label}
              >
                <Currency currency={amount.currency} value={amount.amount} />
              </ItemTotalDetail>
            );
          })}
        </div>
        <hr className="tw-border-gray-300" />
        <div className="tw-p-2">
          <ItemTotalDetail
            className="tw-text-sm"
            label={
              <FormattedMessage id="OrderView.FulfillmentCard.itemMerchandiseAdjustmentsTotal" />
            }
          >
            <Currency
              currency={merchandiseAdjustmentsTotal.currency}
              value={merchandiseAdjustmentsTotal.amount}
            />
          </ItemTotalDetail>
        </div>
      </PopoverLabel>
    </div>
  );
};

const AttributeChoices = ({ orderItem }) => {
  const { attributeChoices } = orderItem;
  if (isEmpty(attributeChoices)) {
    return null;
  }
  return (
    <ul className="tw-mt-2 tw-list-none tw-text-sm">
      {map(attributeChoices, (attributeChoice, attributeName) => (
        <li key={attributeName}>
          <span
            className="tw-font-semibold tw-text-gray-700"
            title={attributeChoice.optionLabel || attributeName}
          >{`${attributeChoice.optionLabel || attributeName}: `}</span>
          <span
            className="tw-font-medium tw-text-gray-600"
            title={attributeChoice.label || attributeChoice.value}
          >
            {attributeChoice.label || attributeChoice.value}
          </span>
        </li>
      ))}
    </ul>
  );
};

const ItemTotalDetail = ({ children, className, label }) => {
  return (
    <div className={cx(className, 'tw-flex tw-justify-between')}>
      <div className="tw-text-gray-700">{label}</div>
      <div className="tw-ml-2 tw-font-medium tw-text-gray-900">{children}</div>
    </div>
  );
};

export default AuroraFulfillmentCardItem;
export { AuroraFulfillmentCardItem };
